import { INLINE_LINK } from '@/components/bedrock';
import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { useT } from '@/core/I18nProvider';
import { LegalPage } from '@/features/legal/LegalPage';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import type { ReactElement } from 'react';

/**
 * Refund & Cancellation Policy (`/trust/refunds`).
 *
 * The plain-language refund + cancellation surface. It is required for
 * payment-processor (Paddle merchant-of-record) website approval and is the
 * baseline a paid SaaS must publish. This page is the projection of the
 * refund/cancellation clauses ALREADY in the Terms of Service (§ "Fees,
 * billing, and refunds" + § Term) and the /pricing cancellation FAQ — the
 * Terms remain the binding instrument; this page never introduces a commercial
 * fact that isn't in them.
 *
 * **Scope discipline** (same bar as the Terms page): links ONLY to routes that
 * exist (/trust, /terms, /pricing) and routes refund requests to
 * admin@syncanix.com rather than a dead URL. Tier-specific commercials are NOT
 * enumerated here (they live at /pricing, the single source of truth).
 *
 * **Three-layer contract preserved**: pure presentational. No data fetch.
 */
export function Refunds(): ReactElement {
  const { t } = useT();
  useLocalizedSeoMeta({
    title: t('refunds.meta.title'),
    description: t('refunds.meta.description'),
    path: '/trust/refunds',
  });
  return (
    <LegalPage
      breadcrumb={t('refunds.breadcrumb')}
      title={t('refunds.title')}
      intro={
        <>
          {t('refunds.intro.before')}{' '}
          <a className={INLINE_LINK} href="mailto:admin@syncanix.com">
            admin@syncanix.com
          </a>{' '}
          {t('refunds.intro.after')}
        </>
      }
      lastUpdatedSlug="refunds"
    >
      <section aria-labelledby="refunds-cancel" className="mb-10">
        <h2
          id="refunds-cancel"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('refunds.cancel.heading')}
        </h2>
        <p>{t('refunds.cancel.body')}</p>
      </section>

      <section aria-labelledby="refunds-monthly" className="mb-10">
        <h2
          id="refunds-monthly"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('refunds.monthly.heading')}
        </h2>
        <p>{t('refunds.monthly.body')}</p>
      </section>

      <section aria-labelledby="refunds-annual" className="mb-10">
        <h2
          id="refunds-annual"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('refunds.annual.heading')}
        </h2>
        <p>{t('refunds.annual.body')}</p>
      </section>

      <section aria-labelledby="refunds-issue" className="mb-10">
        <h2
          id="refunds-issue"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('refunds.issue.heading')}
        </h2>
        <p>{t('refunds.issue.body')}</p>
      </section>

      <section aria-labelledby="refunds-disputes" className="mb-10">
        <h2
          id="refunds-disputes"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('refunds.disputes.heading')}
        </h2>
        <p>{t('refunds.disputes.body')}</p>
      </section>

      <section aria-labelledby="refunds-free" className="mb-10">
        <h2
          id="refunds-free"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('refunds.free.heading')}
        </h2>
        <p>{t('refunds.free.body')}</p>
      </section>

      <section aria-labelledby="refunds-request" className="mb-10">
        <h2
          id="refunds-request"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('refunds.request.heading')}
        </h2>
        <p>
          {t('refunds.request.before')}{' '}
          <a className={INLINE_LINK} href="mailto:admin@syncanix.com">
            admin@syncanix.com
          </a>{' '}
          {t('refunds.request.after')}
        </p>
      </section>

      <section aria-labelledby="refunds-related">
        <h2
          id="refunds-related"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('refunds.related.heading')}
        </h2>
        <ul className="list-disc space-y-1 ps-6">
          <li>
            <Link className={INLINE_LINK} to="/terms">
              {t('refunds.related.terms.linkText')}
            </Link>{' '}
            {t('refunds.related.terms.desc')}
          </li>
          <li>
            <Link className={INLINE_LINK} to="/pricing">
              {t('refunds.related.pricing.linkText')}
            </Link>{' '}
            {t('refunds.related.pricing.desc')}
          </li>
        </ul>
      </section>
    </LegalPage>
  );
}
